<script setup lang="ts">
import { PropType, computed, ref, onMounted, inject, nextTick } from 'vue'
import { IElement } from '@gohighlevel/ghl-proposals-common'
import CWrapper from './CWrapper.vue'
import { useMarkerForImage } from '@/composable/useMarkerForImage'
import { useDocument } from '@/composable'
import { useBlockElements } from '@/composable/useBlockElements'

const props = defineProps({
  element: {
    type: Object as PropType<IElement>,
    required: true,
  },
})

const imageRef = ref<HTMLImageElement | null>(null)
const wrapperRef = ref<HTMLDivElement | null>(null)
const isPDFView = inject<boolean>('isPDFView')
const { documentVersion } = useDocument()
const { addBlockElement } = useBlockElements()
const { createMarkers } = useMarkerForImage(
  wrapperRef as any,
  imageRef as any,
  props.element
)

const customClassesForImg = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return [styles?.align, styles?.imageEffect]
})

const customStyleForImageParent = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return {
    backgroundColor: styles?.backgroundColor,
    marginTop: styles?.marginTop,
    marginBottom: styles?.marginBottom,
    marginLeft: styles?.marginLeft,
    marginRight: styles?.marginRight,
    paddingTop: styles?.paddingTop,
    paddingBottom: styles?.paddingBottom,
    paddingLeft: styles?.paddingLeft,
    paddingRight: styles?.paddingRight,
  }
})

// Update markers when image loads or window resizes
const updateMarkers = () => {
  requestAnimationFrame(createMarkers)
}

onMounted(() => {
  if (imageRef.value && (documentVersion.value ?? 1) > 1 && isPDFView) {
    imageRef.value.addEventListener('load', updateMarkers)
  }

  nextTick(() => {
    addBlockElement(props.element.id, props.element.type)
  })
})
</script>

<template>
  <CWrapper
    class="el-wrapper img-el print:break-inside-void break-inside-avoid"
  >
    <div ref="wrapperRef" :style="customStyleForImageParent" class="wrapper">
      <img
        ref="imageRef"
        :src="element?.component?.options?.src"
        :alt="element?.component?.options?.altText"
        class="img"
        :class="customClassesForImg"
        :height="props.element?.responsiveStyles.large?.height"
        :width="props.element?.responsiveStyles.large?.width"
        aria-hidden="true"
      />
    </div>
  </CWrapper>
</template>

<style lang="scss">
.el-wrapper {
  .img {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
}
</style>
